import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import Stack from '@mui/material/Stack'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useGanttChartOptions, { GANTT_CHART_COLORS } from './useGanttChartOptions'
import GanttChartHeader from './GanttChartHeader'

import { ReactComponent as CircleIcon } from '../../../assets/images/circle.svg'
import { Activity } from '../../queries/useGetActivitiesQuery'
import { EmptyPlaceholder } from '../EmptyPlaceholder'
import { SimpleLegend } from '../DonutCharts/components/SimpleLegend'
import { useChartLegend } from '../../hooks/useChartLegend'
import { useTheme } from '@mui/styles'
import useActivitiesDisplayName from '../../hooks/useDisplayNames/variants/useActivitiesDisplayName'
import { useIsMobileDevice } from '../../hooks/useIsMobileDevice'
import { DonutLegendItem } from '../DonutCharts'

type GanttChartProps = {
    activities: Activity[]
    isInteractive?: boolean
}

const GanttChart = ({ activities, isInteractive = true }: GanttChartProps) => {
    const theme = useTheme()

    const isMobileDevice = useIsMobileDevice()

    const activitiesDisplayName = useActivitiesDisplayName()

    const ganttChartOptions = useGanttChartOptions(activities, isInteractive)
    const [chart, setChart] = useState<Highcharts.Chart | null>(null)

    const chartRef = useCallback((chartComponent: HighchartsReact.RefObject) => {
        if (chartComponent && chartComponent.chart) {
            setChart(chartComponent.chart)
        }
    }, [])

    const legendItems = useMemo(
        (): DonutLegendItem[] =>
            (ganttChartOptions?.series || []).map((series, index) => ({
                id: series.id || 'Unclassified',
                label: series.name || 'Unclassified',
                color: GANTT_CHART_COLORS[index] || theme.palette.figma.grayscale.tier4,
                icon: CircleIcon,
            })),
        [ganttChartOptions?.series]
    )

    const totalCount = activities?.length || 0

    const { hiddenItems, toggleItem, focusItem } = useChartLegend(legendItems, chart)

    const focusItemRef = useRef(focusItem)
    useEffect(() => {
        focusItemRef.current = focusItem
    }, [focusItem])

    useEffect(() => {
        if (chart && isInteractive) {
            chart.update({
                plotOptions: {
                    gantt: {
                        point: {
                            events: {
                                click: function () {
                                    focusItemRef.current(this.series.index)
                                },
                            },
                        },
                    },
                },
            })
        }
    }, [chart, isInteractive])

    const isGanttChartDataEmpty = !ganttChartOptions?.series?.length

    return (
        <Stack flex={1} direction="column" gap={1} px={2} py={2.75} bgcolor="figma.grayscale.tier7" borderRadius="16px">
            {isGanttChartDataEmpty && (
                <EmptyPlaceholder
                    title=""
                    subtitle={
                        // The header (and its tooltip) isn't rendered when nothing is
                        // drawable, so the reason has to live here instead.
                        totalCount > 0
                            ? `0/${totalCount} ${activitiesDisplayName.plural} showing — none have both a start and an end date`
                            : `No ${activitiesDisplayName.plural} to display`
                    }
                />
            )}
            {!isGanttChartDataEmpty && (
                <>
                    <GanttChartHeader series={ganttChartOptions?.series} totalCount={totalCount}>
                        <SimpleLegend
                            items={legendItems}
                            hiddenItems={hiddenItems}
                            onItemClick={isInteractive ? toggleItem : undefined}
                            direction={isMobileDevice ? 'column' : 'row'}
                        />
                    </GanttChartHeader>
                    <Stack flex={1} direction="column">
                        <HighchartsReact
                            highcharts={Highcharts}
                            constructorType="ganttChart"
                            options={ganttChartOptions}
                            ref={chartRef}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    )
}

export default GanttChart
